import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface Campaign {
    campanhaId: number;
    campanhaNome: string;
    campanhaTemplateNome: string;
    campanhaTemplateIdioma: string;
    campanhaCategoria: CampanhaCategoria;
    campanhaStatus: CampanhaStatus;
    campanhaAgendadaEm: string | null;
    /**
     * O nome REAL do campo que a API manda (`CampanhaResponse.RedirecionarRespostaParaIA`),
     * conferido na resposta de `BuscarTodos`. Ele não leva o prefixo `campanha`
     * que os irmãos levam.
     */
    redirecionarRespostaParaIA: boolean;
    /**
     * @deprecated Nunca existiu na resposta — lia `undefined`, que o formulário
     * antigo transformava em `false` e gravava de volta, DESLIGANDO o Agente de
     * IA a cada edição. Fica só enquanto `CampaignPage.tsx` (a tela legada)
     * existir; some com ela.
     */
    campanhaRedirecionarRespostaParaIA?: boolean;
    campanhaCreatedat: string;
    /** Quando o disparo foi posto na fila. `null` em rascunho. */
    campanhaEnvioIniciadoEm?: string | null;
    /** Quando o último da fila foi processado. `null` enquanto `Enviando`. */
    campanhaEnvioFinalizadoEm?: string | null;
    /** Não vem em `BuscarTodos` nem em `ResumoMetricas`. */
    campanhaUpdatedat?: string;
}

/**
 * O que `ResumoMetricas` devolve, e SÓ isso — conferido em
 * `CampanhaResumoResponse.cs` e na resposta real.
 *
 * Repare no que NÃO está aqui: idioma do template, agendamento e a flag da IA.
 * O tipo antigo (`CampaignMetrics`, abaixo) herdava de `Campaign` e prometia os
 * três, então a tela escrevia `template · idioma` com o idioma vazio e abria a
 * edição a partir deste objeto — o que zerava a flag da IA no `PUT`. Quem
 * precisa da campanha inteira usa `buscarPorId`.
 */
export interface CampanhaResumo {
    campanhaId: number;
    campanhaNome: string;
    campanhaTemplateNome: string;
    campanhaCategoria: CampanhaCategoria;
    campanhaStatus: CampanhaStatus;
    campanhaCreatedat: string;
    /** Já processados (enviados + com falha). Não conta quem ainda está na fila. */
    totalDisparado: number;
    /** Ainda na fila. Maior que zero só com a campanha em `Enviando`. */
    totalPendentes: number;
    totalComInteracao: number;
    totalEntregues: number;
    totalLidas: number;
    percentualInteracao: number;
}

/**
 * @deprecated Promete campos que `ResumoMetricas` não devolve. Existe só para
 * `CampaignPage.tsx` (a tela legada) continuar compilando; use
 * `CampanhaResumo`.
 */
export interface CampaignMetrics extends Campaign {
    totalDisparado: number;
    totalComInteracao: number;
    totalEntregues?: number;
    totalLidas?: number;
    percentualInteracao: number;
}

export type CampanhaStatus = 'Rascunho' | 'Enviando' | 'Finalizada';
export type CampanhaCategoria = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

export interface CreateCampaignDTO {
    nome: string;
    templateNome: string;
    templateIdioma: string;
    categoria: CampanhaCategoria;
    agendadaEm?: string;
    redirecionarRespostaParaIA: boolean;
}

export interface UpdateCampaignDTO {
    campanhaId: number;
    nome: string;
    templateNome: string;
    templateIdioma: string;
    categoria: CampanhaCategoria;
    status: CampanhaStatus;
    agendadaEm?: string;
    redirecionarRespostaParaIA: boolean;
}

export interface Destinatario {
    destinatarioId: number;
    numero: string | null;
    bsuid: string | null;
    variaveis: string[] | null;
}

export interface DestinatarioInput {
    numero?: string;
    bsuid?: string;
    variaveis?: string[];
}

export interface ParametroTemplate {
    tipo: 'text' | 'image' | 'video' | 'document' | 'coupon_code' | 'location';
    texto?: string;
    codigoCupom?: string;
    imagem?: { id?: string; link?: string };
    video?: { id?: string; link?: string };
    documento?: { id?: string; link?: string };
    /**
     * Cabeçalho de localização. O tipo não declarava, então a tela antiga
     * montava o payload como `any` — e um campo errado aqui só aparecia como
     * falha vinda da Meta, depois do disparo.
     */
    localizacao?: { latitude: string; longitude: string; nome: string; endereco: string };
}

export interface ComponenteTemplate {
    tipo: 'HEADER' | 'BODY' | 'BUTTON';
    subTipo?: 'COPY_CODE' | 'URL' | 'QUICK_REPLY';
    indicesBotao?: number;
    parametros?: ParametroTemplate[];
}

export interface DispararDTO {
    destinatarios?: DestinatarioInput[];
    componentes?: ComponenteTemplate[];
}

/**
 * O disparo só põe na fila e volta na hora; quem envia é o servidor, em
 * segundo plano. Os números do envio vêm de `buscarProgresso`.
 */
export interface DispararResult {
    campanhaId: number;
    /** Destinatários que entraram na fila. */
    totalNaFila: number;
    /** Descartados antes da fila: sem número nem BSUID. */
    totalIgnorados: number;
}

/** `GET /{id}/progresso` — o que a tela mostra enquanto a campanha está em `Enviando`. */
export interface CampanhaProgresso {
    campanhaId: number;
    campanhaStatus: CampanhaStatus;
    total: number;
    /** Ainda vão sair (na fila ou saindo agora). */
    pendentes: number;
    enviados: number;
    falhas: number;
    entregues: number;
    lidas: number;
    /** (total - pendentes) / total * 100, com 1 casa. */
    percentual: number;
    /** `true` quando a campanha saiu de `Enviando`: hora de parar de perguntar. */
    concluido: boolean;
    iniciadoEm: string | null;
    finalizadoEm: string | null;
}

/**
 * O status do disparo fala DUAS línguas, e isso não é bug de dado: o disparo
 * nasce `pendente`, o envio em segundo plano passa por `processando` e grava
 * `enviado`/`falha` (`CampanhaEnvioService`), e o webhook da Meta
 * SOBRESCREVE com o vocabulário dela — `sent`, `delivered`, `read`, `failed`
 * (`CampanhaDisparoRepositorio.AtualizarStatus`). Medido na base: os quatro
 * valores convivem.
 *
 * `entregue` e `lido` em português nunca são escritos por ninguém — estavam no
 * tipo antigo e não existem. Use `rotuloDisparo()` para exibir.
 */
export type DisparoStatus =
    | 'pendente' | 'processando'
    | 'enviado' | 'falha'
    | 'sent' | 'delivered' | 'read' | 'failed';

const DISPARO_ROTULO: Record<DisparoStatus, { label: string; tom: 'fila' | 'enviado' | 'entregue' | 'lido' | 'falha' }> = {
    /* A fila do disparo assíncrono: existe antes de a mensagem sair. */
    pendente: { label: 'Na fila', tom: 'fila' },
    processando: { label: 'Enviando', tom: 'fila' },
    enviado: { label: 'Enviado', tom: 'enviado' },
    sent: { label: 'Enviado', tom: 'enviado' },
    delivered: { label: 'Entregue', tom: 'entregue' },
    read: { label: 'Lido', tom: 'lido' },
    falha: { label: 'Falha', tom: 'falha' },
    failed: { label: 'Falha', tom: 'falha' },
};

/** Rótulo em português e o tom da etiqueta, para qualquer das duas línguas. */
export const rotuloDisparo = (status: string) =>
    DISPARO_ROTULO[status as DisparoStatus] ?? { label: status, tom: 'enviado' as const };

export interface Disparo {
    disparoId: number;
    contatoId: number | null;
    conversaId: number | null;
    disparoNumero: string;
    /** Ver `DisparoStatus`: pode vir em português ou no vocabulário da Meta. */
    disparoStatus: string;
    teveInteracao: boolean;
    disparoEnviadoEm: string;
    disparoEntregueEm: string | null;
    disparoLidoEm: string | null;
}

export interface ConversasResult {
    itens: Disparo[];
    total: number;
    pagina: number;
    porPagina: number;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export const CampaignService = {
    getAll: async (): Promise<ApiResponse<Campaign[]>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/BuscarTodos`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return res.json();
        return res.json();
    },

    getMetrics: async (): Promise<ApiResponse<CampaignMetrics[]>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/ResumoMetricas`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    /** O mesmo endpoint do `getMetrics`, com o tipo honesto. */
    buscarResumo: async (): Promise<ApiResponse<CampanhaResumo[]>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/ResumoMetricas`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    /**
     * A campanha inteira, com idioma e flag da IA — o resumo não tem os dois.
     *
     * A API não expõe `BuscarPorId`, então filtra do `BuscarTodos`. É uma
     * chamada só e a lista é pequena; se um dia crescer, o endpoint novo entra
     * aqui sem mexer em quem chama.
     */
    buscarPorId: async (id: number): Promise<ApiResponse<Campaign | null>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/BuscarTodos`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        const corpo: ApiResponse<Campaign[]> = await res.json();
        if (!corpo.sucesso) return { ...corpo, dados: null };
        const achada = (corpo.dados ?? []).find(c => c.campanhaId === id) ?? null;
        return {
            sucesso: achada !== null,
            mensagem: achada ? corpo.mensagem : 'Campanha não encontrada.',
            dados: achada,
        };
    },

    create: async (dto: CreateCampaignDTO): Promise<ApiResponse<Campaign>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    update: async (dto: UpdateCampaignDTO): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/Atualizar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    remove: async (id: number): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/Remover/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    getDestinatarios: async (campanhaId: number): Promise<ApiResponse<Destinatario[]>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${campanhaId}/destinatarios`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    addDestinatarios: async (campanhaId: number, destinatarios: DestinatarioInput[]): Promise<ApiResponse<{ total: number }>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${campanhaId}/destinatarios`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ destinatarios }),
        });
        return res.json();
    },

    removeDestinatario: async (campanhaId: number, destinatarioId: number): Promise<ApiResponse<null>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${campanhaId}/destinatarios/${destinatarioId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    disparar: async (id: number, dto: DispararDTO): Promise<ApiResponse<DispararResult>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${id}/disparar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    buscarProgresso: async (campanhaId: number): Promise<ApiResponse<CampanhaProgresso>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${campanhaId}/progresso`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },

    getConversas: async (campanhaId: number, pagina = 1, porPagina = 20): Promise<ApiResponse<ConversasResult>> => {
        const res = await apiFetch(`${API_URL}/api/campanha/${campanhaId}/conversas?pagina=${pagina}&porPagina=${porPagina}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        return res.json();
    },
};
